import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { brl, formatDate, formatDateTime, statusLabel, statusColor } from "@/lib/format";
import { Package, X, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["aguardando_pagamento","pago","em_separacao","enviado","entregue","cancelado","devolvido"];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [tracking, setTracking] = useState("");

  const load = () => api.get("/admin/orders", { params: filter?{status_filter:filter}:{} }).then((r)=>setOrders(r.data));
  useEffect(() => { load(); }, [filter]);

  const changeStatus = async (status) => {
    await api.put(`/admin/orders/${selected.id}/status`, { status, tracking_code: tracking || null });
    toast.success("Status atualizado");
    const { data } = await api.get(`/orders/${selected.id}`);
    setSelected(data); load();
  };

  return (
    <div className="space-y-5" data-testid="admin-orders">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl text-[#4A3B40]">Pedidos</h1>
        <select value={filter} onChange={(e)=>setFilter(e.target.value)} className="bg-white border border-[#F9E1E8] rounded-full px-4 py-2 text-sm outline-none" data-testid="admin-orders-filter">
          <option value="">Todos</option>
          {STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-[#F9E1E8] p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-[#8C7A80] uppercase border-b border-[#F9E1E8]">
              <th className="pb-2">Pedido</th><th className="pb-2">Cliente</th><th className="pb-2">Data</th><th className="pb-2">Pagamento</th><th className="pb-2">Status</th><th className="pb-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} onClick={()=>{setSelected(o); setTracking(o.tracking_code||"");}} className="border-b border-[#F9E1E8] last:border-0 cursor-pointer hover:bg-[#FFF9FA]" data-testid={`admin-order-row-${o.order_number}`}>
                <td className="py-3 font-semibold text-[#C9708B]">{o.order_number}</td>
                <td>{o.customer_name}</td>
                <td className="text-[#8C7A80]">{formatDate(o.created_at)}</td>
                <td className="text-[#8C7A80] uppercase text-xs">{o.payment_method}</td>
                <td><span className={`text-xs px-2 py-1 rounded-full border ${statusColor(o.status)}`}>{statusLabel(o.status)}</span></td>
                <td className="text-right font-semibold">{brl(o.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {orders.length === 0 && <p className="text-center text-[#8C7A80] py-10">Nenhum pedido encontrado.</p>}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="order-detail-modal">
          <div className="absolute inset-0 bg-black/40" onClick={()=>setSelected(null)} />
          <div className="relative bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-serif text-2xl text-[#4A3B40]">Pedido {selected.order_number}</h2>
                <p className="text-sm text-[#8C7A80]">{formatDateTime(selected.created_at)}</p>
              </div>
              <button onClick={()=>setSelected(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <h3 className="font-serif text-[#4A3B40] font-semibold mb-2">Cliente</h3>
                <p className="text-sm">{selected.customer_name}</p>
                <p className="text-sm text-[#8C7A80]">{selected.customer_email}</p>
                <p className="text-sm text-[#8C7A80]">{selected.customer_phone} • CPF: {selected.customer_cpf}</p>
                <a href={`https://wa.me/55${(selected.customer_phone||"").replace(/\D/g,"")}`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm text-[#7DBE9E] font-semibold hover:underline"><MessageCircle className="w-4 h-4" /> Chamar no WhatsApp</a>
              </div>
              <div>
                <h3 className="font-serif text-[#4A3B40] font-semibold mb-2">Entrega</h3>
                <p className="text-sm">{selected.address.street}, {selected.address.number} {selected.address.complement}</p>
                <p className="text-sm text-[#8C7A80]">{selected.address.neighborhood}, {selected.address.city} - {selected.address.state}</p>
                <p className="text-sm text-[#8C7A80]">CEP: {selected.address.cep}</p>
                <p className="text-sm mt-2"><strong>Método:</strong> {selected.shipping_method?.toUpperCase()}</p>
              </div>
            </div>

            <div className="mt-5">
              <h3 className="font-serif text-[#4A3B40] font-semibold mb-2">Itens</h3>
              {selected.items.map((it, i) => (
                <div key={i} className="flex gap-3 py-2 border-b border-[#F9E1E8] last:border-0">
                  <img src={it.image} className="w-12 h-12 rounded-lg object-cover" alt="" />
                  <div className="flex-1 text-sm"><p>{it.name}</p><p className="text-xs text-[#8C7A80]">{it.quantity}x {brl(it.unit_price)}</p></div>
                  <p className="font-semibold">{brl(it.line_total)}</p>
                </div>
              ))}
              <div className="text-right pt-3 space-y-1 text-sm">
                <p>Subtotal: <strong>{brl(selected.subtotal)}</strong></p>
                {selected.discount > 0 && <p className="text-[#7DBE9E]">Cupom: -{brl(selected.discount)}</p>}
                {selected.pix_discount > 0 && <p className="text-[#7DBE9E]">PIX: -{brl(selected.pix_discount)}</p>}
                <p>Frete: <strong>{selected.shipping_cost===0?'Grátis':brl(selected.shipping_cost)}</strong></p>
                <p className="text-lg font-bold text-[#C9708B]">Total: {brl(selected.total)}</p>
              </div>
            </div>

            <div className="mt-5 bg-[#FFF9FA] rounded-xl p-4">
              <h3 className="font-serif text-[#4A3B40] font-semibold mb-3">Atualizar status</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {STATUSES.map((s) => (
                  <button key={s} onClick={()=>changeStatus(s)} className={`text-xs px-3 py-1.5 rounded-full border ${selected.status===s?statusColor(s)+' font-bold':'bg-white border-[#F9E1E8] text-[#8C7A80] hover:border-[#C9708B]'}`} data-testid={`admin-order-status-${s}`}>
                    {statusLabel(s)}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={tracking} onChange={(e)=>setTracking(e.target.value)} placeholder="Código de rastreio" className="flex-1 border border-[#F9E1E8] bg-white rounded-xl px-3 py-2 text-sm outline-none" data-testid="admin-order-tracking" />
                <button onClick={()=>changeStatus(selected.status)} className="btn-lara-soft text-sm py-2 px-4">Salvar rastreio</button>
              </div>
            </div>

            <div className="mt-4">
              <h3 className="font-serif text-[#4A3B40] font-semibold mb-2">Linha do tempo</h3>
              <div className="space-y-2">
                {(selected.timeline||[]).map((t, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-[#C9708B] mt-1.5" />
                    <div><p className="font-semibold">{statusLabel(t.status)}</p><p className="text-xs text-[#8C7A80]">{formatDateTime(t.at)} • {t.note}</p></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
